<div class="vendor-po-upload p-3" *ngIf="vendor">
	<h5 class="mb-2">Upload PO</h5>
	<p class="text-muted small mb-3">
		Upload one CSV or Excel file. For workbooks with several tabs (e.g. one sheet per brand), choose the sheet. If row 1 is a title and real headers are on row 2, set
		<strong>Header row #</strong> to <strong>2</strong>, then click <strong>Parse spreadsheet</strong>. Map columns and build lines.
	</p>

	<div class="mb-3">
		<div class="d-flex align-items-center flex-wrap gap-2 mb-2">
			<button mat-stroked-button type="button" (click)="downloadPoUploadTemplate()">
				<mat-icon>file_download</mat-icon>
				Download template
			</button>
		</div>
		<ngx-dropzone
			class="inv-upload-dropzone"
			[ngClass]="'inv-upload-dropzone--' + dropzoneThemeColor"
			[multiple]="false"
			[expandable]="true"
			accept=".csv,.xlsx,.xls,text/csv,text/plain,application/csv,text/x-csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
			(change)="onDropzoneChange($event)"
			[disabled]="processing">
			<ngx-dropzone-label>
				<div class="vendor-po-dropzone-inner">
					<mat-icon class="vendor-po-dropzone-icon">cloud_upload</mat-icon>
					<span class="vendor-po-dropzone-title">Drop file here or click</span>
					<span class="vendor-po-dropzone-hint text-muted small">One file — .csv, .xlsx, or .xls</span>
				</div>
			</ngx-dropzone-label>
			<ngx-dropzone-preview *ngFor="let f of files" [file]="f" [removable]="true" (removed)="onRemove(f)">
				<ngx-dropzone-label class="vendor-po-file-label">{{ f.name }}</ngx-dropzone-label>
			</ngx-dropzone-preview>
		</ngx-dropzone>

		<div *ngIf="processing && !uploadReady" class="mt-2 d-flex align-items-center text-muted small">
			<mat-spinner diameter="22" class="mr-2"></mat-spinner>
			Working…
		</div>
	</div>

	<div *ngIf="showParseOptions && activeFile" class="border rounded p-3 mb-3 bg-light">
		<h6 class="small font-weight-bold text-uppercase text-muted mb-3">Parse options</h6>
		<p class="small text-muted mb-2" *ngIf="uploadReady">Changing sheet or header row and parsing again replaces the mapping grid and built lines.</p>
		<div class="row">
			<div class="col-md-6 mb-3" *ngIf="sheetNames.length">
				<mat-form-field appearance="outline" class="w-100">
					<mat-label>Sheet</mat-label>
					<mat-select [(value)]="selectedSheetName">
						<mat-option *ngFor="let s of sheetNames" [value]="s">{{ s }}</mat-option>
					</mat-select>
					<mat-hint>Workbook has {{ sheetNames.length }} sheet(s)</mat-hint>
				</mat-form-field>
			</div>
			<div class="col-md-6 mb-3">
				<mat-form-field appearance="outline" class="w-100">
					<mat-label>Header row #</mat-label>
					<input matInput type="number" min="1" [(ngModel)]="headerRowNumber" name="poHeaderRow" />
					<mat-hint>Row that contains column titles (e.g. 2 if row 1 is a label like “BRAND: …”)</mat-hint>
				</mat-form-field>
			</div>
		</div>
		<button mat-raised-button color="primary" type="button" (click)="parseSpreadsheet()" [disabled]="processing">
			<mat-icon>table_view</mat-icon>
			Parse spreadsheet
		</button>
	</div>

	<div *ngIf="uploadReady && uploadedRawData.length" class="table-responsive">
		<p class="small text-muted mb-2">Adjust column targets if needed, then build lines. Currency symbols and commas in prices are stripped when building.</p>
		<table class="table table-sm table-striped vendor-po-map-table">
			<thead>
				<tr>
					<th *ngFor="let h of uploadedHeaders" class="small align-top">
						<div class="text-muted text-truncate mb-1" [attr.title]="h">{{ h }}</div>
						<mat-form-field appearance="outline" class="w-100 vendor-po-map-field">
							<mat-select [(value)]="columnMappings[h]" (selectionChange)="columnMappings[h] = $event.value">
								<mat-option *ngFor="let type of availableFieldTypes" [value]="type">{{ fieldTypeLabel(type) }}</mat-option>
							</mat-select>
						</mat-form-field>
					</th>
				</tr>
			</thead>
			<tbody>
				<tr *ngFor="let row of uploadedRawData; let i = index" [class.table-primary]="i < 3">
					<td *ngFor="let h of uploadedHeaders">{{ row[h] }}</td>
				</tr>
			</tbody>
		</table>
		<div class="d-flex align-items-center mt-3 flex-wrap gap-2">
			<button mat-raised-button color="accent" type="button" (click)="buildLines()" [disabled]="!hasRequiredMappings()">
				Build lines
			</button>
			<button mat-stroked-button type="button" (click)="clearAll()">Clear</button>
		</div>

		<div *ngIf="builtLines.length" class="mt-4">
			<h6 class="mb-2">PO lines</h6>
			<div class="table-responsive">
				<table class="table table-sm table-bordered">
					<thead>
						<tr>
							<th>Item (stockid)</th>
							<th>Quantity</th>
							<th>Price</th>
							<th>Description</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						<tr *ngFor="let line of builtLines; let i = index">
							<td><input class="form-control form-control-sm" [(ngModel)]="line.stockid" name="sid{{ i }}" placeholder="stockid" /></td>
							<td>
								<input class="form-control form-control-sm" type="number" step="0.01" [(ngModel)]="line.quantity" name="sqty{{ i }}" placeholder="qty" />
							</td>
							<td>
								<input class="form-control form-control-sm" type="number" step="0.01" [(ngModel)]="line.price" name="spr{{ i }}" placeholder="price" />
							</td>
							<td><input class="form-control form-control-sm" [(ngModel)]="line.description" name="sdesc{{ i }}" placeholder="description" /></td>
							<td>
								<button mat-icon-button color="warn" type="button" (click)="removeLine(i)" matTooltip="Remove line">
									<mat-icon>delete</mat-icon>
								</button>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div class="d-flex align-items-center mt-2 flex-wrap gap-2">
				<button mat-stroked-button type="button" (click)="addLine()"><mat-icon>add</mat-icon> Add line</button>
				<button mat-raised-button color="primary" type="button" (click)="createPo()" [disabled]="creating">
					<ng-container *ngIf="!creating">Create PO</ng-container>
					<ng-container *ngIf="creating">Creating…</ng-container>
				</button>
				<span class="text-success" *ngIf="poSuccess">{{ poSuccess }}</span>
				<span class="text-danger" *ngIf="poError">{{ poError }}</span>
			</div>
		</div>
	</div>
</div>

<div *ngIf="!vendor" class="p-3 text-muted">Select a vendor to upload a PO.</div>
